import { Database, ScanSearch } from 'lucide-react'
import { AssetsToolMessage } from '../ScreenataChatToolMessages'
import {
  BagDelegate,
  formatToolValue,
  getToolCallId,
  isToolError,
  normalizeTone,
  pickString,
  plural,
  readToolState,
  ToolDetailOverlayLink,
  ToolListPreview,
  ToolStatusLine,
  type ToolPartProps,
} from './_base'

const dbIcon = <Database className='h-3.5 w-3.5 shrink-0 text-text-placeholder' />

export function AssetsListToolMessage({ part }: ToolPartProps) {
  const state = readToolState(part)
  const count =
    typeof state.result?.count === 'number' ? state.result.count : null
  const assets: Array<Record<string, unknown>> = Array.isArray(
    state.result?.assets
  )
    ? state.result.assets
    : []
  return (
    <>
      <ToolStatusLine
        state={state}
        loadingLabel='Loading assets...'
        completedLabel={
          count !== null ? `${plural(count, 'asset')} loaded` : 'Assets loaded'
        }
        completedDetail={pickString(
          state.args?.classification,
          state.args?.assetType
        )}
        completedIcon={dbIcon}
      />
      {state.isComplete && !isToolError(state) ? (
        <ToolListPreview
          emptyLabel='No assets found'
          items={assets.map((asset) => ({
            id: pickString(asset.displayId),
            title: pickString(asset.name) ?? 'Untitled asset',
            subtitle: pickString(asset.vendorName, asset.ownerLabel),
            meta: [asset.assetType, asset.criticality, asset.environment],
            tone: normalizeTone(asset.criticality),
          }))}
        />
      ) : null}
    </>
  )
}

export function AssetsGetToolMessage({ part }: ToolPartProps) {
  const state = readToolState(part)
  const technicalOwner =
    typeof state.result?.technicalOwner?.firstName === 'string' ||
    typeof state.result?.technicalOwner?.lastName === 'string'
      ? `${state.result?.technicalOwner?.firstName ?? ''} ${state.result?.technicalOwner?.lastName ?? ''}`.trim()
      : null
  return (
    <>
      <ToolStatusLine
        state={state}
        loadingLabel='Loading asset...'
        completedLabel='Asset loaded'
        loadingDetail={pickString(state.args?.assetId)}
        completedDetail={pickString(state.result?.name, state.args?.assetId)}
        completedIcon={dbIcon}
      />
      {state.isComplete && !isToolError(state) && state.result ? (
        <ToolDetailOverlayLink
          overlayId={getToolCallId(
            part,
            `asset-${pickString(state.result.displayId, state.args?.assetId) ?? 'detail'}`
          )}
          payload={{
            entityType: 'asset',
            toolName: 'assetsGet',
            title: pickString(state.result.name) ?? 'Untitled asset',
            subtitle: pickString(state.result.displayId, state.args?.assetId),
            description: pickString(state.result.description),
            fields: [
              {
                label: 'Type',
                value: formatToolValue(state.result.assetType),
              },
              {
                label: 'Criticality',
                value: formatToolValue(state.result.criticality),
                tone: normalizeTone(state.result.criticality),
              },
              {
                label: 'Classification',
                value: formatToolValue(state.result.dataClassification),
              },
              {
                label: 'Provider',
                value: formatToolValue(state.result.provider),
              },
              {
                label: 'Owner',
                value: pickString(
                  state.result.ownerLabel,
                  state.result.owner?.name,
                  technicalOwner
                ),
              },
              {
                label: 'Vendor',
                value: pickString(state.result.vendor?.name),
              },
            ],
            sections: [
              {
                title: 'Linked risks',
                content: Array.isArray(state.result.risks)
                  ? state.result.risks
                      .map((link: Record<string, unknown>) =>
                        pickString(
                          (link.risk as Record<string, unknown> | undefined)
                            ?.title,
                          link.title,
                          link.riskId
                        )
                      )
                      .filter(Boolean)
                      .join('\n')
                  : null,
              },
              {
                title: 'Child assets',
                content: Array.isArray(state.result.childAssets)
                  ? state.result.childAssets
                      .map((asset: Record<string, unknown>) =>
                        pickString(asset.name, asset.id)
                      )
                      .filter(Boolean)
                      .join('\n')
                  : null,
              },
            ],
          }}
          systemDetail={
            pickString(state.result.id, state.args?.assetId)
              ? {
                  entityType: 'asset',
                  entityId: pickString(state.result.id, state.args?.assetId)!,
                  workspaceId: '',
                }
              : null
          }
        />
      ) : null}
    </>
  )
}

export function AssetsSearchToolMessage({ part }: ToolPartProps) {
  const state = readToolState(part)
  const count =
    typeof state.result?.count === 'number' ? state.result.count : null
  const assets: Array<Record<string, unknown>> = Array.isArray(
    state.result?.assets
  )
    ? state.result.assets
    : []
  return (
    <>
      <ToolStatusLine
        state={state}
        loadingLabel='Searching assets...'
        completedLabel={
          count !== null
            ? `Found ${plural(count, 'asset')}`
            : 'Asset search complete'
        }
        loadingDetail={pickString(state.args?.query)}
        completedDetail={count === 0 ? null : pickString(state.args?.query)}
        completedIcon={dbIcon}
      />
      {state.isComplete && !isToolError(state) ? (
        <ToolListPreview
          emptyLabel='No matching assets'
          items={assets.map((asset) => ({
            id: pickString(asset.displayId),
            title: pickString(asset.name) ?? 'Untitled asset',
            meta: [asset.assetType, asset.criticality, asset.environment],
            tone: normalizeTone(asset.criticality),
          }))}
        />
      ) : null}
    </>
  )
}

export function AssetsStatsToolMessage({ part }: ToolPartProps) {
  const state = readToolState(part)
  const total =
    typeof state.result?.total === 'number' ? state.result.total : null
  return (
    <ToolStatusLine
      state={state}
      loadingLabel='Loading asset stats...'
      completedLabel={
        total !== null
          ? `${plural(total, 'asset')} tracked`
          : 'Asset stats loaded'
      }
      completedIcon={dbIcon}
    />
  )
}

// analyzeGap renders a rich coverage UI in the bag.
export function AssetsAnalyzeGapToolMessage(props: ToolPartProps) {
  return (
    <BagDelegate Component={AssetsToolMessage} action='analyzeGap' {...props} />
  )
}

export function AssetsCreateToolMessage({ part }: ToolPartProps) {
  const state = readToolState(part)
  return (
    <ToolStatusLine
      state={state}
      loadingLabel='Creating asset...'
      completedLabel='Asset created'
      loadingDetail={pickString(state.args?.name)}
      completedDetail={pickString(state.args?.name, state.result?.name)}
      completedIcon={dbIcon}
    />
  )
}

export function AssetsUpdateToolMessage({ part }: ToolPartProps) {
  const state = readToolState(part)
  return (
    <ToolStatusLine
      state={state}
      loadingLabel='Updating asset...'
      completedLabel='Asset updated'
      completedDetail={pickString(state.args?.assetId)}
      completedIcon={dbIcon}
    />
  )
}

export function AssetsClassifyToolMessage({ part }: ToolPartProps) {
  const state = readToolState(part)
  return (
    <ToolStatusLine
      state={state}
      loadingLabel='Classifying asset...'
      completedLabel='Asset classified'
      completedDetail={pickString(
        state.args?.classification,
        state.args?.assetId
      )}
      completedIcon={dbIcon}
    />
  )
}

export function AssetsMarkReviewedToolMessage({ part }: ToolPartProps) {
  const state = readToolState(part)
  return (
    <ToolStatusLine
      state={state}
      loadingLabel='Marking asset reviewed...'
      completedLabel='Asset marked reviewed'
      completedDetail={pickString(state.args?.assetId)}
      completedIcon={dbIcon}
    />
  )
}

export function AssetsListProvidersToolMessage({ part }: ToolPartProps) {
  const state = readToolState(part)
  const count = Array.isArray(state.result?.providers)
    ? state.result.providers.length
    : null
  return (
    <ToolStatusLine
      state={state}
      loadingLabel='Loading asset providers...'
      completedLabel={
        count !== null ? `${plural(count, 'provider')}` : 'Providers loaded'
      }
      completedIcon={dbIcon}
    />
  )
}

export function AssetsSyncNowToolMessage({ part }: ToolPartProps) {
  const state = readToolState(part)
  return (
    <ToolStatusLine
      state={state}
      loadingLabel='Syncing assets...'
      completedLabel='Asset sync complete'
      loadingDetail={pickString(state.args?.provider)}
      completedDetail={pickString(state.args?.provider)}
      completedIcon={
        <ScanSearch className='h-3.5 w-3.5 shrink-0 text-text-placeholder' />
      }
    />
  )
}
